import React from "react";
import { motion } from "framer-motion";
import { Calendar, Check, X, Loader2, Lock } from "lucide-react";
import MotionButton from "./motion/MotionButton";
import { SPRING_TAP } from "../lib/motionVariants";

const LETTERS = ["A", "B", "C", "D", "E"];
export const FREE_EXPLANATION_CHARS = 140;

const formatDate = (isoDate) => {
  if (!isoDate) return "";
  const d = new Date(`${isoDate}T00:00:00Z`);
  return d.toLocaleDateString("it-IT", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const optionClass = ({ revealed, isCorrect, isSelectedWrong, isSelected }) => {
  if (revealed) {
    if (isCorrect) return "border-brand-green bg-brand-green/10 text-brand-primary";
    if (isSelectedWrong) return "border-brand-red bg-brand-red/8 text-brand-primary";
    return "border-brand-border opacity-55 text-brand-secondary";
  }
  if (isSelected) return "border-brand-green bg-brand-green/10 text-brand-primary";
  return "border-brand-border bg-brand-surface hover:border-brand-secondary text-brand-primary";
};

export function DailyHeader({ date, qIdx, total, scoreSoFar }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
      <div className="flex items-center gap-2 text-brand-secondary">
        <Calendar className="h-4 w-4 text-brand-green" />
        <span className="eyebrow text-brand-green">// Domande del giorno</span>
        <span className="text-xs text-brand-secondary capitalize hidden sm:inline">· {formatDate(date)}</span>
      </div>
      <div className="flex items-center gap-3 text-xs text-brand-secondary">
        <span data-testid="daily-progress">
          Domanda <span className="font-semibold text-brand-primary">{qIdx + 1}</span> di {total}
        </span>
        <span data-testid="daily-score" className="border border-brand-border rounded-full px-2.5 py-0.5">
          ✓ {scoreSoFar} / {total}
        </span>
      </div>
    </div>
  );
}

export function DailyOptions({ q, selected, setSelected, revealed }) {
  return (
    <div className="mt-6 space-y-2.5">
      {q.options.map((opt, i) => {
        const isCorrect = revealed && i === q.correct_index;
        const isSelectedWrong = revealed && i === selected && i !== q.correct_index;
        return (
          <motion.button
            key={`${q.question_id}-opt-${i}`}
            data-testid={`daily-option-${i}`}
            disabled={revealed}
            onClick={() => setSelected(i)}
            whileTap={{ scale: 0.98 }}
            transition={SPRING_TAP}
            className={`w-full text-left border rounded-xl px-4 py-3.5 transition-colors duration-150 flex items-start gap-3 ${optionClass({
              revealed, isCorrect, isSelectedWrong, isSelected: selected === i,
            })}`}
          >
            <span className="font-serif text-base mt-0.5 text-brand-secondary">{LETTERS[i]}.</span>
            <span className="text-sm sm:text-base flex-1 leading-relaxed">{opt}</span>
            {isCorrect && <Check className="h-4 w-4 text-brand-green mt-0.5 shrink-0" />}
            {isSelectedWrong && <X className="h-4 w-4 text-brand-red mt-0.5 shrink-0" />}
          </motion.button>
        );
      })}
    </div>
  );
}

export function ExplanationBlock({ explanation, isAuthenticated, onUnlock }) {
  const truncate = !isAuthenticated && explanation.length > FREE_EXPLANATION_CHARS;
  const shown = truncate ? `${explanation.slice(0, FREE_EXPLANATION_CHARS).trimEnd()}…` : explanation;
  return (
    <div className="mt-4 border border-brand-border bg-brand-background rounded-xl p-4">
      <div className="eyebrow mb-2">// Spiegazione</div>
      <p className="text-sm text-brand-primary/85 leading-relaxed" data-testid="daily-explanation">
        {shown}
      </p>
      {truncate && (
        <button
          data-testid="daily-unlock-explanation"
          onClick={onUnlock}
          className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-brand-green gold-link"
        >
          <Lock className="h-3 w-3" /> Sblocca la spiegazione completa
        </button>
      )}
    </div>
  );
}

export function LoadingState() {
  return (
    <div className="flex items-center justify-center py-16 text-brand-secondary">
      <Loader2 className="h-5 w-5 animate-spin mr-2" /> Caricamento delle domande di oggi...
    </div>
  );
}

export function SecondaryCTA({ user, onClick }) {
  return (
    <div className="mt-6 text-center">
      <button
        data-testid="daily-skip-cta"
        onClick={onClick}
        className="gold-link text-xs text-brand-secondary inline-flex items-center gap-1.5"
      >
        <Lock className="h-3 w-3" />
        {user ? "Sblocca tutte le domande premium" : "Sblocca tutte le domande con Google"}
      </button>
    </div>
  );
}

export function VerifyButton({ disabled, onClick }) {
  return (
    <MotionButton
      data-testid="daily-verify-btn"
      className="mt-7"
      disabled={disabled}
      onClick={onClick}
    >
      Verifica risposta
    </MotionButton>
  );
}
